import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { Icon } from "@iconify/react";
import { enqueueSnackbar } from "notistack";
import {
  ChangeReviewFacts,
  normalizeChangeOrder,
} from "../Change/ChangeReviewCard";
import getChangeRequest from "../../../../controllers/booking/postTicketing/change/getChangeRequest";
import startOpenTicketProcessing from "../../../../controllers/booking/postTicketing/change/startOpenTicketProcessing";
import proceedOpenTicketWithSupplier from "../../../../controllers/booking/postTicketing/change/proceedOpenTicketWithSupplier";
import recordOpenTicketOutcome from "../../../../controllers/booking/postTicketing/change/recordOpenTicketOutcome";
import denyChangeTicketRequest from "../../../../controllers/booking/postTicketing/change/denyChangeTicketRequest";
import declineChangeBySupplier from "../../../../controllers/booking/postTicketing/change/declineChangeBySupplier";
import ChangeMessagesModal from "../Change/ChangeMessagesModal";
import {
  OPEN_TICKET_OUTCOME,
  getOpenTicketReviewGuidance,
} from "../../../../data/flight/order/getChangeAdminGuidance";

export default function OpenTicketReviewModal({
  open,
  setOpen,
  data,
  onDone,
  step = "review",
  onProceed,
  onOpened,
}) {
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [accepting, setAccepting] = useState(false);
  const [declining, setDeclining] = useState(false);
  const [recording, setRecording] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [messagesOpen, setMessagesOpen] = useState(false);
  const [messagesData, setMessagesData] = useState(null);

  useEffect(() => {
    if (!open) {
      setRejectReason("");
      setOrder(null);
      setRecording(false);
      if (!messagesOpen) {
        setMessagesData(null);
      }
      return;
    }
    const raw = typeof data === "object" ? data : { _id: data };
    setOrder(normalizeChangeOrder(raw));
    if (!raw?._id) return;

    let cancelled = false;
    setLoading(true);
    getChangeRequest(raw._id)
      .then((res) => {
        if (cancelled) return;
        if (res?.return) setOrder(normalizeChangeOrder(res.data));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, data, messagesOpen]);

  function close() {
    if (accepting || declining || recording) return;
    setOpen?.(false);
  }

  async function handleCanBeOpened() {
    if (!order?._id) return;
    setAccepting(true);
    const res = await startOpenTicketProcessing(order._id).finally(() =>
      setAccepting(false),
    );
    if (res.return) {
      enqueueSnackbar(
        "Ops is processing this. Talk to the airline next. There is no quote.",
        { variant: "success" },
      );
      setOpen?.(false);
      onDone?.();
    } else enqueueSnackbar(res.msg, { variant: "error" });
  }

  async function handleProceed() {
    if (!order?._id) return;
    if (onProceed) {
      setOpen?.(false);
      onProceed(order);
      return;
    }
    setAccepting(true);
    const res = await proceedOpenTicketWithSupplier(order._id).finally(() =>
      setAccepting(false),
    );
    if (res.return) {
      enqueueSnackbar(
        "Finish it with the airline, then record the result. There is no quote.",
        { variant: "success" },
      );
      setOpen?.(false);
      onDone?.();
    } else enqueueSnackbar(res.msg, { variant: "error" });
  }

  async function handleNotEligible() {
    if (!order?._id) return;
    if (!rejectReason.trim()) {
      enqueueSnackbar("Say why this ticket cannot be opened.", {
        variant: "warning",
      });
      return;
    }
    setDeclining(true);
    const reason = rejectReason.trim();
    const res = await recordOpenTicketOutcome(order._id, {
      outcome: OPEN_TICKET_OUTCOME.NOT_ELIGIBLE,
      reason,
    }).finally(() => setDeclining(false));
    if (res.return) {
      enqueueSnackbar("Marked not eligible. The agent will see the reason.", {
        variant: "success",
      });
      setOpen?.(false);
      onDone?.();
    } else enqueueSnackbar(res.msg, { variant: "error" });
  }

  async function handleAirlineDeclined() {
    if (!order?._id) return;
    if (!rejectReason.trim()) {
      enqueueSnackbar("Write the airline's reason.", { variant: "warning" });
      return;
    }
    setRecording(true);
    const reason = rejectReason.trim();
    const res = await declineChangeBySupplier(order._id, { reason }).finally(
      () => setRecording(false),
    );
    if (res.return) {
      enqueueSnackbar("Recorded as airline declined.", { variant: "success" });
      setOpen?.(false);
      onDone?.();
    } else enqueueSnackbar(res.msg, { variant: "error" });
  }

  async function handleCouldNotFinish() {
    if (!order?._id) return;
    if (!rejectReason.trim()) {
      enqueueSnackbar("Say what went wrong. This is not an airline decline.", {
        variant: "warning",
      });
      return;
    }
    setRecording(true);
    const reason = rejectReason.trim();
    const res = await recordOpenTicketOutcome(order._id, {
      outcome: OPEN_TICKET_OUTCOME.PROCESSING_FAILED,
      reason,
    }).finally(() => setRecording(false));
    if (res.return) {
      enqueueSnackbar(
        "Recorded as could not finish. Stay on this request and try again.",
        { variant: "success" },
      );
      setOpen?.(false);
      onDone?.();
    } else enqueueSnackbar(res.msg, { variant: "error" });
  }

  async function handleNeedMoreInfo() {
    if (!order?._id) return;
    if (!rejectReason.trim()) {
      enqueueSnackbar("Say what the airline asked for.", {
        variant: "warning",
      });
      return;
    }
    setRecording(true);
    const reason = rejectReason.trim();
    const res = await recordOpenTicketOutcome(order._id, {
      outcome: OPEN_TICKET_OUTCOME.ADDITIONAL_INFO,
      reason,
    }).finally(() => setRecording(false));
    if (res.return) {
      enqueueSnackbar(
        "Need more information. Get it, then proceed again. This is not an airline decline.",
        { variant: "success" },
      );
      setOpen?.(false);
      onDone?.();
    } else enqueueSnackbar(res.msg, { variant: "error" });
  }

  async function handleCloseRequest() {
    if (!order?._id) return;
    if (!rejectReason.trim()) {
      enqueueSnackbar("Say why you are closing this request.", {
        variant: "warning",
      });
      return;
    }
    setDeclining(true);
    const res = await denyChangeTicketRequest(order._id, {
      rejectReason: rejectReason.trim(),
    }).finally(() => setDeclining(false));
    if (res.return) {
      enqueueSnackbar("Request closed.", { variant: "success" });
      setOpen?.(false);
      onDone?.();
    } else enqueueSnackbar(res.msg, { variant: "error" });
  }

  function handleOpened() {
    if (!order?._id) return;
    if (onOpened) {
      setOpen?.(false);
      onOpened(order);
    }
  }

  if (!open && !messagesOpen) return null;

  const busy = accepting || declining || recording;
  const review = getOpenTicketReviewGuidance(order || data);
  const isSupplier = step === "supplier" || review.trigger === "with_supplier";
  const isProcessing =
    step === "processing" || review.trigger === "ops_checking";
  const needsReason =
    review.showNotEligible ||
    review.showAirlineDeclined ||
    review.showCouldNotFinish ||
    review.showNeedMoreInfo ||
    review.showClose;
  const isTerminal =
    ["not_eligible", "airline_declined", "successful", "completed"].includes(
      review.trigger,
    );

  const reviewModal = open ? ReactDOM.createPortal(
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4">
      <button
        type="button"
        aria-label="Close overlay"
        className="absolute inset-0 bg-black/40"
        onClick={close}
      />
      <div className="relative w-full max-w-[640px] max-h-[90vh] overflow-y-auto rounded-2xl bg-white shadow-2xl">
        <div className="flex items-start justify-between gap-4 px-6 pt-5">
          <div>
            <h2 className="text-[17px] font-semibold text-gray-900 leading-6">
              {review.title}
            </h2>
            <p className="mt-1 text-sm text-gray-500">
              Read why you are here, then check the airline before you continue.
              There is no quote and no charge.
            </p>
          </div>
          <button
            type="button"
            onClick={close}
            className="mt-0.5 rounded-full p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
          >
            <Icon icon="material-symbols:close" className="text-xl" />
          </button>
        </div>

        <div className="px-6 pb-6 pt-4">
          {loading && !order?.flightBooking && !order?.originalItinerary ? (
            <div className="flex items-center justify-center py-10">
              <div className="primaryLoader" />
            </div>
          ) : (
            <ChangeReviewFacts data={order} showQuestions={false} />
          )}

          <p className="mt-4 text-sm text-gray-700">
            We do not notify you when the agent writes here.{" "}
            <button
              type="button"
              onClick={() => {
                if (busy) return;
                setMessagesData(order || data);
                setMessagesOpen(true);
                setOpen?.(false);
              }}
              disabled={busy}
              className="text-[#1D4ED8] underline font-medium hover:text-[#1E40AF] disabled:opacity-60"
            >
              Check your messages
            </button>
          </p>

          <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 px-3 py-3">
            <div className="text-sm font-semibold text-amber-900">
              {review.whyTitle}
            </div>
            {review.statusDisplay ? (
              <p className="mt-1 text-xs font-medium text-amber-800">
                Status: {review.statusDisplay}
              </p>
            ) : null}
            <p className="mt-1 text-sm text-amber-900/90">{review.why}</p>
            {review.note ? (
              <p className="mt-1 text-xs text-amber-800">
                System note: {review.note}
              </p>
            ) : null}
            {review.steps?.length ? (
              <>
                <div className="mt-3 text-sm font-semibold text-gray-900">
                  Do this
                </div>
                <ol className="mt-1 list-decimal pl-5 space-y-1 text-sm text-gray-800">
                  {review.steps.map((stepItem) => (
                    <li key={stepItem}>{stepItem}</li>
                  ))}
                </ol>
              </>
            ) : null}
          </div>

          {review.questions?.length ? (
            <div className="mt-4 text-sm text-gray-700">
              <div className="font-medium text-gray-900 mb-1">Ask yourself</div>
              <ol className="list-decimal pl-5 space-y-1">
                {review.questions.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ol>
            </div>
          ) : null}

          {needsReason ? (
            <div className="mt-4">
              <label className="text-sm font-medium text-gray-800">
                {isSupplier
                  ? "If the airline said no, asked for more information, or you could not finish, say why"
                  : "If it is not eligible, say why"}
              </label>
              <textarea
                className="mt-1 w-full min-h-[88px] rounded-xl border border-gray-200 px-3 py-2 text-sm text-gray-800 outline-none focus:border-primary1"
                placeholder={
                  isSupplier
                    ? "e.g. Airline refused to open this fare, asked for the ticket image, or PNR could not be retrieved"
                    : "e.g. Airline does not allow this fare to stay open, ticket already used, passenger is a no-show"
                }
                value={rejectReason}
                onChange={(ev) => setRejectReason(ev.target.value)}
                disabled={busy}
              />
            </div>
          ) : null}

          {isTerminal ? (
            <div className="mt-4">
              <button
                type="button"
                onClick={close}
                className="h-11 w-full rounded-xl border border-gray-200 bg-white text-sm font-semibold text-gray-800 hover:bg-gray-50"
              >
                Close
              </button>
            </div>
          ) : isSupplier || review.showOpened || review.showAirlineDeclined ? (
            <div className="mt-4 flex flex-col gap-2.5">
              {(review.showOpened || isSupplier) && (
                <button
                  type="button"
                  onClick={handleOpened}
                  disabled={busy}
                  className="h-11 w-full rounded-xl bg-primary1 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-60"
                >
                  Opened
                </button>
              )}
              {(review.showAirlineDeclined || isSupplier) && (
                <button
                  type="button"
                  onClick={handleAirlineDeclined}
                  disabled={busy}
                  className="h-11 w-full rounded-xl bg-red-600 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-60"
                >
                  {recording ? (
                    <span className="buttonLoader mx-auto" />
                  ) : (
                    "Airline declined"
                  )}
                </button>
              )}
              {(review.showNeedMoreInfo || isSupplier) && (
                <button
                  type="button"
                  onClick={handleNeedMoreInfo}
                  disabled={busy}
                  className="h-11 w-full rounded-xl border border-gray-200 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60"
                >
                  {recording ? (
                    <span className="buttonLoader mx-auto" />
                  ) : (
                    "Need more information"
                  )}
                </button>
              )}
              {(review.showCouldNotFinish || isSupplier) && (
                <button
                  type="button"
                  onClick={handleCouldNotFinish}
                  disabled={busy}
                  className="h-11 w-full rounded-xl border border-gray-200 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60"
                >
                  {recording ? (
                    <span className="buttonLoader mx-auto" />
                  ) : (
                    "Could not finish"
                  )}
                </button>
              )}
            </div>
          ) : (
            <div className="mt-4 flex flex-col-reverse sm:flex-row gap-2.5">
              {review.showClose ? (
                <button
                  type="button"
                  onClick={handleCloseRequest}
                  disabled={busy}
                  className="h-11 flex-1 rounded-xl bg-red-600 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-60"
                >
                  {declining ? (
                    <span className="buttonLoader mx-auto" />
                  ) : (
                    "Close this request"
                  )}
                </button>
              ) : null}
              {review.showNotEligible ? (
                <button
                  type="button"
                  onClick={handleNotEligible}
                  disabled={busy}
                  className="h-11 flex-1 rounded-xl bg-red-600 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-60"
                >
                  {declining ? (
                    <span className="buttonLoader mx-auto" />
                  ) : (
                    "Not eligible"
                  )}
                </button>
              ) : null}
              {review.showCanOpen || review.showProceed || isProcessing ? (
                <button
                  type="button"
                  onClick={
                    review.showProceed || isProcessing
                      ? handleProceed
                      : handleCanBeOpened
                  }
                  disabled={busy}
                  className="h-11 flex-1 rounded-xl bg-primary1 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-60"
                >
                  {accepting ? (
                    <span className="buttonLoader mx-auto" />
                  ) : review.showProceed || isProcessing ? (
                    "Proceed with the airline"
                  ) : (
                    "This can be opened"
                  )}
                </button>
              ) : null}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body,
  ) : null;

  return (
    <>
      {reviewModal}
      <ChangeMessagesModal
        open={messagesOpen}
        setOpen={(next) => {
          setMessagesOpen(next);
          if (!next) setMessagesData(null);
        }}
        data={messagesData || order || data}
      />
    </>
  );
}
